import { useState } from 'react';
import { Button, Spin } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  LockOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import { CourseTopic, CourseTopicStatus } from './Course.const';
import useGetContractWithProgress from '../../hooks/useGetContractWithProgress';
import useGetThemeContent from '../../hooks/useGetThemeContent';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';

import css from './Course.module.css';

// We support a minimal markdown subset only — inline images (![alt](url)),
// explicit line breaks (<br>), ATX headings (#..######), and GFM tables. Raw
// HTML is never injected: the only tokens we recognise are images, <br>, and
// headings, each emitted as a controlled React element. Image URLs are
// restricted to http(s) for XSS safety; everything else falls back to text.
const INLINE_TOKEN_REGEX = /!\[([^\]]*)\]\(([^)\s]+)\)|<br\s*\/?>/gi;
const HEADING_REGEX = /^(#{1,6})\s+(.+)$/;

const isSafeImageUrl = (url: string): boolean => /^https?:\/\//i.test(url);

// Render inline text with markdown images and <br> line breaks. Returns [] when
// there is nothing to transform, so callers can render the raw string instead.
const renderInline = (text: string, keyPrefix: string): JSX.Element[] => {
  const nodes: JSX.Element[] = [];
  let lastIndex = 0;
  let partIdx = 0;
  let match: RegExpExecArray | null;
  INLINE_TOKEN_REGEX.lastIndex = 0;
  while ((match = INLINE_TOKEN_REGEX.exec(text)) !== null) {
    const full = match[0];
    const before = text.slice(lastIndex, match.index);
    if (before) nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{before}</span>);
    if (full[0] === '!') {
      const alt = match[1];
      const url = match[2];
      if (isSafeImageUrl(url)) {
        nodes.push(
          <img key={`${keyPrefix}i${partIdx++}`} src={url} alt={alt} className={css.ContentImage} />
        );
      } else {
        nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{full}</span>);
      }
    } else {
      nodes.push(<br key={`${keyPrefix}b${partIdx++}`} />);
    }
    lastIndex = match.index + full.length;
  }
  const tail = text.slice(lastIndex);
  if (tail) nodes.push(<span key={`${keyPrefix}t${partIdx++}`}>{tail}</span>);
  return nodes;
};

const renderCell = (text: string, keyPrefix: string): JSX.Element[] | string => {
  const nodes = renderInline(text, keyPrefix);
  return nodes.length ? nodes : text;
};

// Split a GFM table row into trimmed cells, dropping the outer pipes.
const splitTableRow = (line: string): string[] => {
  let l = line.trim();
  if (l.startsWith('|')) l = l.slice(1);
  if (l.endsWith('|')) l = l.slice(0, -1);
  return l.split('|').map(c => c.trim());
};

// Detect a GFM table: a header row, a delimiter row (---, :--, --:, :--:),
// then zero or more body rows. Returns null when the segment isn't a table.
const parseTable = (
  seg: string
): { header: string[]; rows: string[][] } | null => {
  const lines = seg.split('\n').map(l => l.trim()).filter(Boolean);
  if (lines.length < 2 || !lines[0].includes('|')) return null;
  const delim = splitTableRow(lines[1]);
  if (delim.length === 0 || !delim.every(c => /^:?-+:?$/.test(c))) return null;
  return {
    header: splitTableRow(lines[0]),
    rows: lines.slice(2).map(splitTableRow),
  };
};

const renderContentSegment = (seg: string, segIdx: number): JSX.Element[] => {
  const table = parseTable(seg);
  if (table) {
    return [
      <table key={`tbl${segIdx}`} className={css.ContentTable}>
        <thead>
          <tr>
            {table.header.map((cell, i) => (
              <th key={i}>{renderCell(cell, `h${segIdx}-${i}-`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c}>{renderCell(cell, `c${segIdx}-${r}-${c}-`)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>,
    ];
  }

  // A single \n\n-delimited segment may interleave headings with prose, so we
  // split on single newlines and emit a heading element per heading line while
  // grouping consecutive prose lines into one paragraph (joined as authored,
  // with <br> tokens providing explicit breaks).
  const blocks: JSX.Element[] = [];
  let para: string[] = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join('\n');
    const key = `p${segIdx}-${blocks.length}`;
    const nodes = renderInline(text, `${key}-`);
    blocks.push(
      <p key={key} className={css.ContentParagraph}>
        {nodes.length ? nodes : text}
      </p>
    );
    para = [];
  };

  seg.split('\n').forEach(line => {
    const heading = HEADING_REGEX.exec(line.trim());
    if (!heading) {
      para.push(line);
      return;
    }
    flushPara();
    const level = Math.min(heading[1].length + 1, 6);
    const Tag = `h${level}` as keyof JSX.IntrinsicElements;
    const key = `h${segIdx}-${blocks.length}`;
    const nodes = renderInline(heading[2], `${key}-`);
    blocks.push(
      <Tag key={key} className={css.ContentHeading}>
        {nodes.length ? nodes : heading[2]}
      </Tag>
    );
  });
  flushPara();

  return blocks;
};

const deriveTopicStatus = (
  themeId: string,
  sortedThemes: CourseTopic[],
  completedThemeIds: Set<string>
): CourseTopicStatus => {
  if (completedThemeIds.has(themeId)) return 'completed';
  const firstIncomplete = sortedThemes.find(t => !completedThemeIds.has(t.id));
  if (firstIncomplete?.id === themeId) return 'current';
  return 'locked';
};

const Course = () => {
  const history = useHistory();
  const { id: contractId } = useParams<{ id: string }>();

  const {
    contract,
    themes,
    listenerUid,
    completedThemeIds,
    markThemeCompleted,
    loading,
  } = useGetContractWithProgress({ contractId });

  const sortedThemes = [...themes].sort((a, b) => a.order - b.order);
  const topics: CourseTopic[] = sortedThemes.map(t => ({
    ...t,
    status: deriveTopicStatus(t.id, sortedThemes, completedThemeIds),
  }));

  const firstUnlocked = topics.find(t => t.status !== 'locked') ?? topics[0];
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const resolvedActiveId = activeTopicId ?? firstUnlocked?.id ?? null;

  const { body, loading: contentLoading } = useGetThemeContent({
    contractId,
    themeId: resolvedActiveId ?? '',
  });

  const completedCount = topics.filter(t => completedThemeIds.has(t.id)).length;
  const total = topics.length;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const activeIndex = topics.findIndex(t => t.id === resolvedActiveId);
  const activeTopic = topics[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === total - 1;

  const goPrev = () => {
    if (!isFirst) setActiveTopicId(topics[activeIndex - 1].id);
  };

  const goNext = async () => {
    if (!activeTopic || !listenerUid) return;
    setSubmitting(true);
    try {
      await api.post(
        `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${listenerUid}/themes/${activeTopic.id}/complete`,
        {}
      );
      markThemeCompleted(activeTopic.id);
      if (isLast) {
        setShowCompletion(true);
      } else {
        setActiveTopicId(topics[activeIndex + 1].id);
      }
    } catch (err: any) {
      const status = err?.statusCode ?? err?.status;
      if (status === 422) {
        toast.showOnFailure('Необходимо пройти предыдущие темы последовательно');
      } else if (status === 402) {
        toast.showOnFailure('Курс недоступен — договор не оплачен');
      } else {
        toast.showOnFailure('Ошибка подтверждения темы');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <Spin />
        </div>
      </PageWrapper>
    );
  }

  if (!topics.length) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <aside className={css.Sidebar}>
            <button
              type='button'
              className={css.BackBtn}
              onClick={() => history.push(APP_URL.MY_COURSES)}
            >
              <ArrowLeftOutlined /> К моим курсам
            </button>
            <div className={css.SidebarHeader}>
              <h2 className={css.SidebarTitle}>Содержание курса</h2>
            </div>
            <p style={{ padding: '16px' }}>Содержание курса не добавлено</p>
          </aside>
          <section className={css.Main}>
            <header className={css.MainHeader}>
              <div className={css.MainHeaderTop}>
                <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              </div>
            </header>
          </section>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper hideLeftPanel>
      <div className={css.Layout}>
        <aside className={css.Sidebar}>
          <button
            type='button'
            className={css.BackBtn}
            onClick={() => history.push(APP_URL.MY_COURSES)}
          >
            <ArrowLeftOutlined /> К моим курсам
          </button>
          <div className={css.SidebarHeader}>
            <h2 className={css.SidebarTitle}>Содержание курса</h2>
            <p className={css.SidebarProgressText}>
              {completedCount} из {total} тем пройдено
            </p>
            <div className={css.ProgressTrack}>
              <div
                className={css.ProgressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <ul className={css.TopicList}>
            {topics.map((topic) => {
              const isActive = topic.id === resolvedActiveId;
              return (
                <li
                  key={topic.id}
                  className={`${css.TopicItem} ${isActive ? css.TopicItemActive : ''}`}
                  onClick={() => {
                    if (topic.status !== 'locked') setActiveTopicId(topic.id);
                  }}
                >
                  <span
                    className={`${css.TopicNumber} ${isActive ? css.TopicNumberActive : ''}`}
                  >
                    {topic.order}
                  </span>
                  <div className={css.TopicInfo}>
                    <div className={css.TopicTitle}>{topic.title}</div>
                    <div className={css.TopicMeta}>—</div>
                  </div>
                  <span className={css.TopicStatus}>
                    {topic.status === 'completed' && (
                      <CheckCircleFilled className={css.IconCompleted} />
                    )}
                    {topic.status === 'locked' && (
                      <LockOutlined className={css.IconLocked} />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className={css.Main}>
          <header className={css.MainHeader}>
            <div className={css.MainHeaderTop}>
              <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              <span className={css.StatusBadge}>
                <ClockCircleOutlined /> {contract?.status ?? ''}
              </span>
            </div>
          </header>

          {showCompletion && (
            <div className={css.CompletionScreen}>
              <TrophyOutlined className={css.CompletionIcon} />
              <h2 className={css.CompletionTitle}>Вы успешно закончили обучение!</h2>
              <p className={css.CompletionText}>
                Вам будет выдан сертификат в ближайшее время.
              </p>
              <div className={css.CompletionActions}>
                <Button
                  className={css.CompletionBtn}
                  onClick={() => {
                    setActiveTopicId(topics[0]?.id ?? null);
                    setShowCompletion(false);
                  }}
                >
                  Вернуться к темам
                </Button>
                <Button
                  type='primary'
                  className={css.CompletionBtn}
                  onClick={() => history.push(APP_URL.MY_COURSES)}
                >
                  К моим курсам
                </Button>
              </div>
            </div>
          )}

          {!showCompletion && activeTopic && (
            <>
              <div className={css.TopicHeader}>
                <div className={css.TopicHeaderMeta}>
                  Тема {activeTopic.order} из {total}
                </div>
                <h2 className={css.TopicHeaderTitle}>{activeTopic.title}</h2>
              </div>

              <div className={css.Content}>
                {contentLoading ? (
                  <Spin />
                ) : (
                  (body ?? '').split('\n\n').flatMap((seg, idx) =>
                    renderContentSegment(seg, idx)
                  )
                )}
              </div>

              <div className={css.Footer}>
                <Button
                  className={css.NavBtn}
                  icon={<ArrowLeftOutlined />}
                  disabled={isFirst || submitting}
                  onClick={goPrev}
                >
                  Назад
                </Button>
                <div className={css.FooterRight}>
                  <Button
                    type='primary'
                    className={css.NextBtn}
                    onClick={goNext}
                    loading={submitting}
                    disabled={!listenerUid || submitting}
                  >
                    {isLast ? 'Завершить курс' : 'Следующая'}
                    {!isLast && !submitting && <ArrowRightOutlined />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </PageWrapper>
  );
};

export default Course;
